import type { MermaidConfig } from "mermaid"

import { renderSanitizedMermaidSvg } from "@/features/mermaid-editor/utils/mermaid-safe-render"
import { logger } from "@/lib/logger"

type MermaidTheme = "default" | "dark" | "neutral" | "forest" | "base"

function getExportConfig(theme: MermaidTheme): MermaidConfig {
  return {
    theme: theme,
    fontFamily: "Arial, sans-serif",
    htmlLabels: false,
    flowchart: { htmlLabels: false, useMaxWidth: false },
    gantt: { useMaxWidth: false },
    sequence: { useMaxWidth: false },
  }
}

function parseSvgNumericAttribute(attributeValue: string | null): number | null {
  if (!attributeValue) return null
  const trimmed = attributeValue.trim()
  if (!trimmed || trimmed === "100%") return null
  const parsed = Number.parseFloat(trimmed)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return parsed
}

function getSvgIntrinsicSize(svgElement: SVGSVGElement): { width: number; height: number } | null {
  const width = parseSvgNumericAttribute(svgElement.getAttribute("width"))
  const height = parseSvgNumericAttribute(svgElement.getAttribute("height"))

  if (width && height) return { width, height }

  const viewBox = svgElement.getAttribute("viewBox")
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/).map((part) => Number.parseFloat(part))
    if (parts.length === 4 && parts.every((part) => Number.isFinite(part))) {
      const [, , viewBoxWidth, viewBoxHeight] = parts
      if (viewBoxWidth > 0 && viewBoxHeight > 0) {
        return { width: viewBoxWidth, height: viewBoxHeight }
      }
    }
  }

  return null
}

function ensureSvgNamespaces(svgElement: SVGSVGElement) {
  if (!svgElement.getAttribute("xmlns")) {
    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg")
  }
  if (!svgElement.getAttribute("xmlns:xlink")) {
    svgElement.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink")
  }
}

function getSvgExportPayload(svg: string): {
  svgForDataUri: string
  width: number
  height: number
} {
  const parsed = new DOMParser().parseFromString(svg, "image/svg+xml")
  const svgRoot = parsed.querySelector("svg")
  if (!(svgRoot instanceof SVGSVGElement)) {
    throw new Error("SVG root not found")
  }

  const svgClone = document.importNode(svgRoot, true)
  ensureSvgNamespaces(svgClone)

  const measurementContainer = document.createElement("div")
  measurementContainer.style.position = "fixed"
  measurementContainer.style.left = "-10000px"
  measurementContainer.style.top = "-10000px"
  measurementContainer.style.width = "0"
  measurementContainer.style.height = "0"
  measurementContainer.style.overflow = "hidden"
  measurementContainer.style.visibility = "hidden"
  measurementContainer.style.pointerEvents = "none"

  document.body.appendChild(measurementContainer)
  measurementContainer.appendChild(svgClone)

  const fallbackSize = { width: 800, height: 600 }
  const size = getSvgIntrinsicSize(svgClone) ?? fallbackSize

  if (!svgClone.getAttribute("viewBox")) {
    svgClone.setAttribute("viewBox", `0 0 ${size.width} ${size.height}`)
  }

  svgClone.setAttribute("width", `${size.width}`)
  svgClone.setAttribute("height", `${size.height}`)

  const svgForDataUri = new XMLSerializer().serializeToString(svgClone)
  try {
    measurementContainer.remove()
  } catch {
    // Container may have been removed already, ignore
  }

  return { svgForDataUri, width: size.width, height: size.height }
}

async function svgToPngBase64(svg: string): Promise<string> {
  const { svgForDataUri, width, height } = getSvgExportPayload(svg)

  const maxCanvasDimension = 8192
  const targetScale = 2
  const scale = Math.max(1, Math.min(targetScale, maxCanvasDimension / Math.max(width, height)))

  const canvas = document.createElement("canvas")
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Could not get canvas context")

  ctx.setTransform(scale, 0, 0, scale, 0, 0)
  ctx.fillStyle = "white"
  ctx.fillRect(0, 0, width, height)

  const img = new Image()
  img.crossOrigin = "anonymous"
  const dataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgForDataUri)}`

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error("Failed to load SVG"))
    img.src = dataUri
  })

  ctx.drawImage(img, 0, 0, width, height)

  return canvas.toDataURL("image/png", 1.0)
}

// Chat answers can carry crafted click directives, and the SVG is attached to the
// live DOM for measuring, so render it strict and sanitized.
async function renderMermaidToSvg(
  code: string,
  diagramId: string,
  theme: MermaidTheme
): Promise<string> {
  const cleanId = `mermaid-export-${diagramId}-${Date.now()}`
  return renderSanitizedMermaidSvg(cleanId, code, getExportConfig(theme))
}

type MermaidCodeBlock = {
  fullMatch: string
  code: string
  startIndex: number
  endIndex: number
}

function extractMermaidBlocks(markdown: string): MermaidCodeBlock[] {
  const blocks: MermaidCodeBlock[] = []
  const regex = /(```|~~~)mermaid\s*\n([\s\S]*?)\1/g

  let match: RegExpExecArray | null
  while ((match = regex.exec(markdown)) !== null) {
    blocks.push({
      fullMatch: match[0],
      code: match[2],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    })
  }

  return blocks
}

export async function replaceMermaidWithImages(
  markdown: string,
  theme: MermaidTheme = "default"
): Promise<string> {
  const blocks = extractMermaidBlocks(markdown)
  if (blocks.length === 0) return markdown

  let result = markdown
  let offset = 0

  for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
    const block = blocks[blockIndex]
    try {
      const svg = await renderMermaidToSvg(block.code, String(blockIndex), theme)
      const pngBase64 = await svgToPngBase64(svg)
      const imageMarkdown = `![Diagram](${pngBase64})`

      const adjustedStart = block.startIndex + offset
      const adjustedEnd = block.endIndex + offset

      result = result.slice(0, adjustedStart) + imageMarkdown + result.slice(adjustedEnd)
      offset += imageMarkdown.length - block.fullMatch.length
    } catch (error) {
      logger.warn(`Failed to render mermaid block ${blockIndex}:`, error as Error)
    }
  }

  return result
}

export function hasMermaidBlocks(markdown: string): boolean {
  return /(```|~~~)mermaid\s*\n[\s\S]*?\1/.test(markdown)
}
